/**
 * Header row / First data row (TMMAPP-0136 item 4b): the text is kept as typed (digits only) and
 * applied on blur or Enter, never clamped per keystroke. An empty or out-of-range value shows
 * "Choose a row between {min} and {max}" on the field and isn't applied. The stepper arrows (an
 * input with no `inputType`) apply at once when in range. A value set from outside (a row picked
 * from the sample, a format, the header's follow) replaces the text.
 */
import { useState, type ChangeEvent } from 'react';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface RowNumberFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  /** The server named this field invalid (`IMPORT_MAPPING_INVALID`). */
  serverError?: string | undefined;
  onApply: (row: number) => void;
}

export function RowNumberField({
  label,
  value,
  min,
  max,
  serverError,
  onApply,
}: RowNumberFieldProps) {
  const { t } = useTranslation();
  const [text, setText] = useState(String(value));
  const [rangeError, setRangeError] = useState(false);
  const [applied, setApplied] = useState(value);
  if (applied !== value) {
    setApplied(value);
    setText(String(value));
    setRangeError(false);
  }

  function inRange(raw: string): number | null {
    if (!/^\d+$/.test(raw)) return null;
    const row = Number(raw);
    return row >= min && row <= max ? row : null;
  }

  function commit(raw: string) {
    const row = inRange(raw);
    if (row === null) {
      setRangeError(true);
      return;
    }
    setRangeError(false);
    if (row !== value) onApply(row);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value.replace(/\D/g, '');
    setText(raw);
    const inputType = (event.nativeEvent as InputEvent).inputType;
    // Typing carries an `inputType`; a stepper arrow doesn't, and applies at once.
    if (typeof inputType !== 'string' || inputType.length === 0) {
      const row = inRange(raw);
      if (row !== null) {
        setRangeError(false);
        if (row !== value) onApply(row);
      }
    }
  }

  const message = rangeError ? t('imports.rowRange', { min, max }) : serverError;
  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={text}
      error={rangeError || serverError !== undefined}
      helperText={message}
      onChange={handleChange}
      onBlur={() => commit(text)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        commit(text);
      }}
      slotProps={{
        htmlInput: { min, max, inputMode: 'numeric' },
        formHelperText: rangeError ? { role: 'alert' } : {},
      }}
    />
  );
}
